import React from "react";
import { Control, FieldErrors, FieldValues, Path } from "react-hook-form";
import { Box } from "@chakra-ui/react";
import { TextFieldControl } from "../SyncConfig/ConfigForm/TextFieldControl";
import { YearAttributeEnum } from "./SisIntegration";
import { CheckBoxControl } from "../SyncConfig/ConfigForm/CheckboxControl";

export type YearAttributes = {
  [key in YearAttributeEnum]?: string;
};

// this contains the default "CEDS" values for the yearr pre-mapped to our enums
export const defaultYearAttributes: YearAttributes = {
  IT: "IT",
  PR: "PR",
  PK: "PK",
  TK: "TK",
  KG: "KG",
  YEAR_01: "01",
  YEAR_02: "02",
  YEAR_03: "03",
  YEAR_04: "04",
  YEAR_05: "05",
  YEAR_06: "06",
  YEAR_07: "07",
  YEAR_08: "08",
  YEAR_09: "09",
  YEAR_10: "10",
  YEAR_11: "11",
  YEAR_12: "12",
  YEAR_13: "13",
  PS: "PS",
  UG: "UG",
  OTHER: "OTHER",
};

// Clever emits its own grade names rather than CEDS codes, so its defaults map those names to our enums
export const defaultCleverYearAttributes: YearAttributes = {
  IT: "InfantToddler",
  PR: "Preschool",
  PK: "PreKindergarten",
  TK: "TransitionalKindergarten",
  KG: "Kindergarten",
  YEAR_01: "1",
  YEAR_02: "2",
  YEAR_03: "3",
  YEAR_04: "4",
  YEAR_05: "5",
  YEAR_06: "6",
  YEAR_07: "7",
  YEAR_08: "8",
  YEAR_09: "9",
  YEAR_10: "10",
  YEAR_11: "11",
  YEAR_12: "12",
  YEAR_13: "13",
  PS: "PostGraduate",
  UG: "Ungraded",
  OTHER: "Other",
};

export const YEAR_LABELS: Record<YearAttributeEnum, string> = {
  [YearAttributeEnum.IT]: "Infant/toddler",
  [YearAttributeEnum.PR]: "Preschool",
  [YearAttributeEnum.PK]: "Prekindergarten",
  [YearAttributeEnum.TK]: "Transitional Kindergarten",
  [YearAttributeEnum.KG]: "Kindergarten",
  [YearAttributeEnum.YEAR_01]: "Year One",
  [YearAttributeEnum.YEAR_02]: "Year Two",
  [YearAttributeEnum.YEAR_03]: "Year Three",
  [YearAttributeEnum.YEAR_04]: "Year Four",
  [YearAttributeEnum.YEAR_05]: "Year Five",
  [YearAttributeEnum.YEAR_06]: "Year Six",
  [YearAttributeEnum.YEAR_07]: "Year Seven",
  [YearAttributeEnum.YEAR_08]: "Year Eight",
  [YearAttributeEnum.YEAR_09]: "Year Nine",
  [YearAttributeEnum.YEAR_10]: "Year Ten",
  [YearAttributeEnum.YEAR_11]: "Year Eleven",
  [YearAttributeEnum.YEAR_12]: "Year Twelve",
  [YearAttributeEnum.YEAR_13]: "Year Thirteen",
  [YearAttributeEnum.PS]: "Postsecondary",
  [YearAttributeEnum.UG]: "Ungraded",
  [YearAttributeEnum.OTHER]: "Other",
};

export const InputToYearAttributes = (values: Record<string, YearAttributeEnum>): YearAttributes => {
  const yearAttributes: YearAttributes = {};
  Object.entries(values).forEach(([key, value]) => {
    const attributeKey = value;
    const currentAttribute = yearAttributes[attributeKey];
    if (currentAttribute) {
      yearAttributes[attributeKey] = `${currentAttribute}, ${key}`;
    } else {
      yearAttributes[attributeKey] = key;
    }
  });

  return yearAttributes;
};

// A sync stored without year mappings reads back with an empty map, so pre-fill the defaults for it
export const StoredOrDefaultYearAttributes = (mappings?: Record<string, YearAttributeEnum>): YearAttributes => {
  return mappings && Object.keys(mappings).length > 0 ? InputToYearAttributes(mappings) : defaultYearAttributes;
};

export const YearAttributesToInput = (
  yearAttributes?: YearAttributes
): { mappings: Record<string, YearAttributeEnum>; duplicateKeys: Set<YearAttributeEnum> } => {
  const mappings: Record<string, YearAttributeEnum> = {};
  const valueToKeys: Record<string, string[]> = {};
  const duplicateKeys = new Set<YearAttributeEnum>();

  if (yearAttributes) {
    Object.entries(yearAttributes).forEach(([key, value]) => {
      // Build token mappings for API (split by comma, keep original case/spacing for token mapping except trim)
      if (value) {
        value.split(",").forEach((v) => {
          const trimmed = v.trim();
          if (trimmed) {
            mappings[trimmed] = key as YearAttributeEnum;

            // add to duplicate check set
            if (!valueToKeys[trimmed]) valueToKeys[trimmed] = [];
            valueToKeys[trimmed].push(key);
          }
        });
      }
    });

    // Compute which keys are involved in duplicates
    Object.values(valueToKeys).forEach((keys) => {
      if (keys.length > 1) {
        keys.forEach((k) => duplicateKeys.add(k as YearAttributeEnum));
      }
    });
  }

  return { mappings, duplicateKeys };
};

type Props<T extends FieldValues> = {
  errors: FieldErrors<T>;
  control: Control<T>;
  clearErrors: (name?: Path<T>) => void;
  syncYearAttributes: boolean;
};

export const AttributeConfig = <T extends FieldValues>({ errors, clearErrors, syncYearAttributes, control }: Props<T>): JSX.Element => {
  const toLabel = (key: YearAttributeEnum): string => {
    return YEAR_LABELS[key] ?? key;
  };

  const yearKeys = Object.values(YearAttributeEnum);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
      }}
    >
      <CheckBoxControl
        fieldProps={{
          name: "sync_year_attributes" as Path<T>,
          testId: "sync-classroom-data-input",
          label: "Sync Year Attributes",
          tooltip:
            "When this is ticked, we will sync year attributes to users. You can override the default mappings below, they accept commas seperated values.",
          control: control,
          errors: errors,
        }}
      />
      {syncYearAttributes && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            height: "300px",
            overflowY: "auto",
            padding: "1rem 2rem",
            border: "1px solid #E2E8F0",
            borderRadius: "6px",
          }}
        >
          {yearKeys.map((key) => (
            <TextFieldControl
              styleType="row-wide"
              fieldProps={{
                name: `year_attribute_mappings.${key}` as Path<T>,
                testId: `year_attribute_mappings.${key}-input`,
                label: toLabel(key),
                control: control,
                errors: errors,
                onChange: () => {
                  clearErrors("sync_year_attributes" as Path<T>);
                },
              }}
              key={key}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
